export const CHANGELOG = [
  {
    version: "3.77.1",
    date: "2026-10-26",
    label: "Security — every app request now requires you to be signed in",
    tag: "fix",
    items: [
      "Kurb's server now checks that every request comes from a signed-in user before it runs AI writing, image generation, or reads and changes your data. This closes a gap where someone who knew the server's address could use Kurb's AI services without logging in.",
      "Website tracking snippets, call and text webhooks, email open/click tracking, unsubscribe links and the WordPress plugin keep working exactly as before — they don't need a login.",
      "Added a fair-use limit on AI writing and image generation per user, so a stolen login can't run up costs. Normal use never comes close to it.",
    ],
  },
  {
    version: "3.77.0",
    date: "2026-08-12",
    label: "Content Agent — reliable WordPress publishing via the Kurb plugin",
    tag: "feature",
    items: [
      "Some hosts (SiteGround, Cloudflare, Wordfence and others) block outside services from publishing through WordPress, which could break Content Agent publishing on those sites. New fix: a lightweight Kurb Publisher plugin you install on your WordPress site once — it pulls approved articles from Kurb and publishes them locally, so publishing works even behind an aggressive host firewall.",
      "Set it up in Content Agent → Configure → WordPress → \"Publishing blocked by your host? Use the Kurb plugin\": download the plugin, upload it to WordPress, paste your connect token, and turn on plugin publishing. Approved articles then publish automatically (title, content, category, tags, and featured image), with no duplicates.",
    ],
  },
  {
    version: "3.76.18",
    date: "2026-07-23",
    label: "Reliability — fixed 10 latent errors found in a full code review",
    tag: "fix",
    items: [
      "Fixed a batch of latent errors left by the modularization, each of which could crash a specific screen or action: accepting a team invite, the WordPress \"Test Connection\" button, Business Brain's \"Open in Campaigns\" link, the GBP Autopilot calendar, the Lead Agent activity list, Inbox review/index cards, and the sign-up/book-demo dialogs on the pricing page. All resolved.",
      "Added an automated undefined-reference check (npm run lint:undef) so this class of bug can't silently ship again.",
    ],
  },
  {
    version: "3.76.17",
    date: "2026-07-23",
    label: "Codebase — Inbox module extracted (modularization complete)",
    tag: "chore",
    items: [
      "Internal: the Inbox moved into its own lazy-loaded file (~900 lines). Every feature cluster is now its own module — App.jsx is down to ~6.1K lines from 61K. No user-facing change.",
    ],
  },
  {
    version: "3.76.16",
    date: "2026-07-23",
    label: "Codebase — Admin Dashboard module extracted",
    tag: "chore",
    items: [
      "Internal: the super-admin Plan Dashboard and all platform views moved into their own lazy-loaded file (~4,620 lines out of App.jsx). App.jsx is now ~7K lines, down from 61K. No user-facing change.",
    ],
  },
  {
    version: "3.76.15",
    date: "2026-07-23",
    label: "Codebase — Image Studio module extracted",
    tag: "chore",
    items: [
      "Internal: Image Studio / Creative Agent moved into its own lazy-loaded file (~1,520 lines out of App.jsx). No user-facing change.",
    ],
  },
  {
    version: "3.76.14",
    date: "2026-07-23",
    label: "Codebase — Conversion + Authority modules extracted",
    tag: "chore",
    items: [
      "Internal: Conversion and Authority Agent moved into their own lazy-loaded files (~2,930 lines out of App.jsx). App.jsx is now ~13K lines, down from 61K. No user-facing change.",
    ],
  },
  {
    version: "3.76.13",
    date: "2026-07-23",
    label: "Codebase — SEO / Index Agent module extracted",
    tag: "chore",
    items: [
      "Internal: SEO, Index Agent, and AI Visibility moved into their own lazy-loaded file (~4,950 lines out of App.jsx). No user-facing change.",
    ],
  },
  {
    version: "3.76.12",
    date: "2026-07-23",
    label: "Codebase — Call Tracking module extracted",
    tag: "chore",
    items: [
      "Internal: Call Tracking moved into its own lazy-loaded file (~2,930 lines out of App.jsx). No user-facing change.",
    ],
  },
  {
    version: "3.76.11",
    date: "2026-07-23",
    label: "Codebase — Lead Agent module extracted",
    tag: "chore",
    items: [
      "Internal: Lead Agent moved into its own lazy-loaded file (~6,100 lines out of App.jsx). No user-facing change.",
    ],
  },
  {
    version: "3.76.10",
    date: "2026-07-23",
    label: "AI assistant module extracted — App.jsx modularization complete",
    tag: "fix",
    items: [
      "Fixed: the Kurb AI chat could hit an internal error while building its system prompt (a missing campaign-templates reference left behind by an earlier refactor). Resolved — AI chat and AI campaign creation are solid again.",
      "Internal: the AI assistant moved into its own file (src/modules/ai.jsx), completing the App.jsx modularization. No other user-facing change.",
    ],
  },
  {
    version: "3.76.9",
    date: "2026-07-22",
    label: "Codebase — Content Agent module extracted",
    tag: "chore",
    items: [
      "Internal: Content Agent (Content Engine) moved into its own lazy-loaded file, and the Topic Distribution / Brand Defaults editors moved to shared. No user-facing change.",
    ],
  },
  {
    version: "3.76.8",
    date: "2026-07-22",
    label: "Codebase — Business Brain module extracted",
    tag: "chore",
    items: [
      "Internal: Business Brain moved into its own lazy-loaded file. No user-facing change.",
    ],
  },
  {
    version: "3.76.7",
    date: "2026-07-22",
    label: "Codebase — Settings module extracted",
    tag: "chore",
    items: [
      "Internal: Settings, Roles & Permissions, Pricing, Brand Guidelines, and Change Log each moved into their own lazy-loaded file. No user-facing change.",
    ],
  },
  {
    version: "3.76.6",
    date: "2026-07-21",
    label: "Codebase — CRM module extracted",
    tag: "chore",
    items: [
      "Internal: CRM moved into its own lazy-loaded file (~4,000 lines out of the main app file). No user-facing change.",
      "Fixed an internal AI-context wiring issue so the assistant sees your contacts while you're on the CRM screen.",
    ],
  },
  {
    version: "3.76.5",
    date: "2026-07-21",
    label: "Codebase — Campaign Manager module extracted",
    tag: "chore",
    items: [
      "Internal: Campaign Manager moved into its own lazy-loaded file. No user-facing change.",
      "Fixed an internal AI-context wiring issue so the assistant sees your campaigns while you're on that screen.",
    ],
  },
  {
    version: "3.76.4",
    date: "2026-07-21",
    label: "Codebase — Content Studio module extracted",
    tag: "chore",
    items: [
      "Internal: Content Studio moved into its own lazy-loaded file. No user-facing change.",
      "Fixed an internal AI-context wiring issue so the assistant sees your content work while you're on that screen.",
      "Fixed a latent bug where opening the ‘Generate Image Package’ dialog from Content Studio or Google Business Profile could error.",
    ],
  },
  {
    version: "3.76.3",
    date: "2026-07-20",
    label: "Codebase — Marketing modules extracted",
    tag: "chore",
    items: [
      "Internal: Social Planner, Ads Manager, Google Business Profile, Email Campaigns, and Blogger Finder each moved into their own lazy-loaded file, trimming the main app file by ~8,000 lines. No user-facing change.",
    ],
  },
  {
    version: "3.76.2",
    date: "2026-07-20",
    label: "Codebase — Media Library module extracted",
    tag: "chore",
    items: [
      "Internal: moved the Media Library into its own lazy-loaded file (third module split). No user-facing change.",
    ],
  },
  {
    version: "3.76.1",
    date: "2026-07-20",
    label: "Codebase — Reviews module extracted",
    tag: "chore",
    items: [
      "Internal: moved the Reviews module into its own lazy-loaded file (second module split after Research), trimming the main app file by ~850 lines. No user-facing change.",
      "Fixed an internal AI-context wiring issue so the assistant reliably sees live Research and Reviews data while you're on those screens.",
    ],
  },
  {
    version: "3.76.0",
    date: "2026-07-18",
    label: "Codebase — modularization foundation",
    tag: "chore",
    items: [
      "Internal: began splitting the single large app file into focused modules (shared design tokens, hooks, and UI components, plus the first lazy-loaded feature module) with a smoke-test safety net. No user-facing change.",
    ],
  },
  {
    version: "3.75.9",
    date: "2026-07-15",
    label: "Lead Agent — Visitors counts now add up",
    tag: "improvement",
    items: [
      "All Visitors numbers are now counted in the same unit (visits), so Identified + Bots + Unmatched always equals 'all visitors' — no more confusing gap.",
      "Unmatched traffic is counted once per visit (not per page view) to match.",
    ],
  },
  {
    version: "3.75.8",
    date: "2026-07-13",
    label: "Lead Agent — reliable filtered-traffic capture",
    tag: "improvement",
    items: [
      "Fixed classification so ISPs and mobile carriers no longer pose as identified companies — they now appear under Unmatched with their real network names.",
      "The Unmatched network breakdown is now captured reliably (dedicated table with atomic counting) so it holds up even under heavy traffic, and its count always matches the list.",
    ],
  },
  {
    version: "3.75.7",
    date: "2026-07-13",
    label: "Lead Agent — clearer name for filtered traffic",
    tag: "improvement",
    items: [
      "Renamed the Visitors \"Unidentified\" segment to \"Unmatched\" — these are real visits from home/mobile/ISP networks that just can't be matched to a company, not a tracking failure.",
    ],
  },
  {
    version: "3.75.6",
    date: "2026-07-13",
    label: "Lead Agent — filtered-traffic network breakdown",
    tag: "improvement",
    items: [
      "The Unidentified segment now lists the actual networks behind your anonymous traffic (ISP/network name, location, page views, last seen) — so you can see what's being filtered out, not just a count.",
      "No individual anonymous visits are stored — only a per-network summary. Detail accrues going forward.",
    ],
  },
  {
    version: "3.75.5",
    date: "2026-07-13",
    label: "Lead Agent — Unidentified visitors segment",
    tag: "improvement",
    items: [
      "The Visitors sidebar now has an 'Unidentified' segment that accounts for anonymous traffic (home WiFi, mobile, unmatched networks), so the 'all visitors' total reconciles with the segments instead of looking like missing data.",
    ],
  },
  {
    version: "3.75.4",
    date: "2026-07-13",
    label: "Lead Agent — visitor re-lookup",
    tag: "improvement",
    items: [
      "A visitor's detail panel now has a ↻ Re-lookup button that refreshes the company name, domain, and bot status from a fresh IP lookup — handy after switching data providers or if a visitor's info was incomplete.",
    ],
  },
  {
    version: "3.75.3",
    date: "2026-07-13",
    label: "Lead Agent — Visitors segment sidebar",
    tag: "improvement",
    items: [
      "The Visitors tab now has a left sidebar to switch between All identified, Returning, High-intent, Bots & crawlers, and Saved-as-lead — each with a live count. The stat row stays on top.",
      "Bots & crawlers are now their own sidebar segment (replacing the show/hide toggle).",
    ],
  },
  {
    version: "3.75.2",
    date: "2026-07-13",
    label: "Lead Agent — visitor IP retention",
    tag: "improvement",
    items: [
      "Visitor IP addresses are now retained for 90 days (then automatically purged) to support company re-lookup and repeat-visitor matching — the aggregate company/visit history is always kept.",
    ],
  },
  {
    version: "3.75.1",
    date: "2026-07-13",
    label: "Lead Agent — clear stale visitor placeholders",
    tag: "improvement",
    items: [
      "One-time cleanup removes old placeholder visitors (the generic network entries from before real company data), so your Visitors list starts fresh with real, named companies only.",
    ],
  },
  {
    version: "3.75.0",
    date: "2026-07-13",
    label: "Lead Agent — real company data for visitors",
    tag: "feature",
    items: [
      "Website visitors are now identified with their real company name and domain (via IPLocate) instead of network placeholders — so you can actually see which businesses visited your site.",
      "Bots and hosting/datacenter traffic are now filtered by an authoritative signal (hosting / VPN / proxy detection), not keyword guessing — far fewer false positives and misses.",
    ],
  },
  {
    version: "3.74.1",
    date: "2026-07-13",
    label: "Lead Agent — broader bot detection",
    tag: "improvement",
    items: [
      "Bot & crawler detection now catches the full range of hosting, datacenter, and telecom providers (e.g. HostRoyale, GTHost, The Constant Company/Vultr, WorldNet, The Media Trust) — not just the big-name clouds.",
      "Your existing visitors are automatically re-checked once, so previously-missed bots move behind the 'Show bots' toggle and out of your identified-company counts.",
    ],
  },
  {
    version: "3.74.0",
    date: "2026-07-12",
    label: "Lead Agent — bot & crawler filtering",
    tag: "feature",
    items: [
      "Website visitors that are search-engine crawlers or cloud-hosting IPs (Googlebot, AWS, Azure, and similar) are now detected and tucked behind a 'Show bots & crawlers' toggle in the Visitors tab — your identified-company counts only reflect real businesses.",
      "Bots no longer trigger 'returning visitor' or 'high-intent' alerts.",
      "Any leads that were saved from bot traffic now show a '🤖 Likely bot' badge in My Leads so they're easy to spot and remove.",
    ],
  },
  {
    version: "3.73.0",
    date: "2026-06-23",
    label: "Authority Agent — Google Stack Builder",
    tag: "feature",
    items: [
      "New 🏗 Stack Builder one-click-creates a Google Drive folder + Doc + Slides + Form under your connected Google account — written with on-brand AI content, cross-linked, made public, and added to your Hub.",
      "Each page links to your website and the other pages, and is auto-tracked in Index Agent.",
      "Quick guided steps to add a Google Site, Drawing, and My Map (these have no Google API).",
      "Monthly build limit is set by your plan so AI costs stay predictable.",
    ],
  },
  {
    version: "3.72.0",
    date: "2026-06-22",
    label: "Authority Agent — Citation Builder",
    tag: "feature",
    items: [
      "New 📈 Citation Builder shows which relevant directories you're missing — 'You're listed on X of Y'.",
      "Guided submit: AI-writes an on-brand listing description, one-click copy your business details, and deep-links to each directory's signup + login.",
      "Track every listing through To do → Submitted → Live, and confirm a listing is live with one click.",
      "Set your business details once (Business Info / NAP) so every listing stays consistent.",
    ],
  },
  {
    version: "3.71.0",
    date: "2026-06-22",
    label: "Authority Agent — check if Citations & Mentions are in Google",
    tag: "feature",
    items: [
      "Citations & Mentions now have a 'Check in Google' button — it tells you whether Google has that external page in its index (✓ Indexed / ✗ Not indexed), using a site: search via DataForSEO.",
      "Works per-row in the item panel or in bulk; results show in a new 'In Google?' column.",
      "Monthly check limit is set by your plan (admin-configurable) so costs stay predictable.",
    ],
  },
  {
    version: "3.70.1",
    date: "2026-06-21",
    label: "Authority Agent — Indexation is owned-only",
    tag: "fix",
    items: [
      "Indexation tracking now applies only to Assets & Properties (URLs you own). Citations & Mentions live on other companies' sites (Yelp, news, etc.), so Google can't index or inspect them — the Indexed column and Request Indexing are no longer shown for those.",
      "The 🔎 Indexation tab now covers Assets & Properties only.",
    ],
  },
  {
    version: "3.70.0",
    date: "2026-06-21",
    label: "Authority Agent SP2 — Indexation Monitor",
    tag: "feature",
    items: [
      "Plain-English indexation status on every Hub item (Indexed / Crawled, not indexed yet / Broken / Checking) — no more raw status codes",
      "Re-check now, Request Indexing, Set priority, and Ignore as bulk actions on Assets / Properties / Citations / Mentions",
      "New 🔎 Indexation tab — every tracked URL across all categories in one monitor, with health tiles you can click to filter",
      "Item detail drawer shows an indexation history timeline (when it was submitted, when it became indexed) with inline Re-check / Request indexing",
    ],
  },
  {
    version: "3.69.0",
    date: "2026-06-20",
    label: "Authority Agent SP1 — Authority Hub MVP",
    tag: "feature",
    items: [
      "New top-level 🛡 Authority Agent module with 8-tab left sidebar (Overview, Assets, Properties, Citations, Mentions, Accounts, Import, Settings)",
      "Workspace-scoped inventory across 5 categories (Assets, Properties, Citations, Mentions, Relationships) with separate Postgres tables",
      "Encrypted credential vault (AES-256-GCM, owner-only password reveal, clipboard-only never DOM)",
      "Smart Import wizard — paste URLs or drop CSV → server auto-classifies against URL patterns + shared Citation Library → preview + confirm → bulk commit",
      "Team-curated Citation Library seeded with 10 high-DA general directories (Yelp/Manta/BBB/HotFrog/etc.); super-admin manages via new Plan Dashboard page with test-classifier tool",
      "Hub items auto-enroll into Index Agent (read-only status badge in list views; manual recheck comes in SP2)",
      "Per-workspace URL cap (default 500) + account cap (default 25) configurable per plan template",
    ],
  },
  {
    version: "3.68.0",
    date: "2026-06-17",
    label: "Platform White Label & Domains",
    tag: "feature",
    items: [
      "New 🌐 super-admin sidebar item — roster of every white-label deployment with status badges",
      "8 deterministic status rules: enabled_no_domain / drift_db_only / vercel_unverified_stale / subdomain_misconfigured / etc.",
      "Operator actions: ↻ Recheck verification (per-row + bulk), Enter workspace →, 🗑 Remove domain, ⊘ Disable white label",
      "Drawer drilldown shows Branding / Custom domain / Subdomain / Email domain cards + status explanation + DNS records",
      "Recheck calls Vercel + Resend live and updates the DB; bulk recheck fans out in batches of 5",
    ],
  },
  {
    version: "3.67.0",
    date: "2026-06-17",
    label: "Platform At-Risk Workspaces",
    tag: "feature",
    items: [
      "New 🚩 super-admin sidebar item — tier counts + signal distribution + filterable workspace table + drill-down drawer",
      "11 deterministic signal rules across 4 categories (engagement decline, never-activated, cap exhaustion, output quality)",
      "Daily snapshot cron + 90-day retention; manual ↻ Recompute now button for impatient cases",
      "Per-workspace 30-day tier history strip + spend trend + last 20 activity events in drawer",
      "Observation only — save attempts happen outside the system in v1",
    ],
  },
  {
    version: "3.66.0",
    date: "2026-06-17",
    label: "Platform Cost & Usage",
    tag: "feature",
    items: [
      "New 💰 super-admin sidebar item — totals, by-provider breakdown, top spenders, daily trend, monthly projection",
      "10 external API providers instrumented (Claude / Twilio / DataForSEO / Apollo / Apify / Resend / Pexels / Pollinations / Placid / Google APIs)",
      "Reports cost section now reads real per-month spend from the new ledger instead of counter-based proxies",
      "Two drill-down drawers: per-provider (operations + workspaces) and per-workspace (provider breakdown + trend)",
      "90-day retention; idempotent nightly rollup keeps dashboard reads fast",
    ],
  },
  {
    version: "3.65.0",
    date: "2026-06-16",
    label: "Platform Reports",
    tag: "feature",
    items: [
      "New 📊 super-admin sidebar item — Growth, Engagement, Output, Cost & Usage, Revenue",
      "Period selector (7d/30d/90d/month/custom) with vs-prior-period Δ on every period-aware metric",
      "MRR / ARR / avg-revenue snapshot from plan_templates × workspaces join, plus per-plan breakdown table",
      "Twilio + Image Pack dollar costs estimated; Claude/Resend/DataForSEO deferred to a future sub-project (Cost & Usage)",
    ],
  },
  {
    version: "3.64.0",
    date: "2026-06-16",
    label: "Platform System Health",
    tag: "feature",
    items: [
      "New 🩺 super-admin sidebar item — status banner, infra cards, cron grid, recent errors, workspaces approaching caps, queue depth, 30-day activity heatmap",
      "Click any cron row → drawer with 14-day trend chart, 7-day success rate, and last 50 runs",
      "30-second auto-refresh + manual ↻ button; stale-response guard prevents auto-refresh racing manual refresh",
      "Reuses activity_log from Activity Log — no new tables, no new instrumentation",
    ],
  },
  {
    version: "3.63.0",
    date: "2026-06-16",
    label: "Platform Activity Log",
    tag: "feature",
    items: [
      "New 📜 Activity top-level item in super admin sidebar — global audit trail of admin actions, cron runs, and user events",
      "Filter sidebar: namespace (admin/cron/user), kind, workspace, actor, date range, full-text search of payload",
      "Per-workspace mini-feed on the Plan Dashboard workspace drawer with deep-link to the global Activity view",
      "10 background crons now record one summary row per run (duration, outcome, counts); 8 admin endpoints + 6 user actions instrumented",
      "90-day retention via daily cleanup cron",
    ],
  },
  {
    version: "3.62.1",
    date: "2026-06-16",
    label: "Bug fixes — Content Agent, Index Agent",
    tag: "fix",
    items: [
      "Content Agent setup no longer 404s — the dated Claude Sonnet snapshot was deprecated server-side, swapped 10 endpoints to claude-sonnet-4-6",
      "Generate-article: doubled max_tokens to 8000 and added a balanced-brace JSON extractor, so long articles no longer fail with \"Could not parse generated article\"",
      "Content Agent setup error now surfaces diagnostic detail (HTTP status, server header, body sample) when the site fetch returns an empty or WAF-challenge page",
      "Index Agent \"Not Indexed\" tile now shows all matching URLs (e.g. 35) instead of just the 1 in the Issues tab — the tile was routing to a stricter filter than its count",
    ],
  },
  {
    version: "3.62.0",
    date: "2026-06-16",
    label: "Super Admin / Agency Admin Users View",
    tag: "feature",
    items: [
      "New 👥 Users top-level sidebar item in admin shells (super admin + agency admin scope)",
      "Search (email + display name), filter by role / admin flag / has workspace, pagination 20/50/100",
      "User detail drawer: profile section, permissions (super only), per-workspace roles, add to workspace, password reset",
      "New endpoints: /api/admin/users, /api/admin/add-user-to-workspace, /api/admin/update-workspace-member-role, /api/admin/remove-workspace-member",
    ],
  },
  {
    version: "3.61.0",
    date: "2026-06-14",
    label: "Quote Card Pack — Creative Agent Wave 3b",
    tag: "feature",
    items: [
      "New 💬 Quote Card Pack product in Creative Agent — 5 brand-styled text cards per source (Headline / Stat / Testimonial / Offer / Tagline)",
      "Studio sub-tab is now a product chooser: Image Package OR Quote Card Pack",
      "Each card uses Brand Kit colors, fonts, and tagline; AI picks text from source content + reviews + Brand Kit",
      "Per-card text editor in side panel: edit & re-render, regenerate text, regenerate image (for Headline / Testimonial)",
      "Push-to-module endpoints use card text (not image prompt) when source is a quote card",
      "Separate plan-tier cap: 20 quote packs/month default",
    ],
  },
  {
    version: "3.60.0",
    date: "2026-06-14",
    label: "Image Package — Creative Agent Wave 3a",
    tag: "feature",
    items: [
      "New 🎨 Creative Agent module — generate a 10-asset branded image package from any article / GBP post / event / free text",
      "✦ Featured image + 3 social graphics + GBP image + 5 ad variations, all sized correctly with Brand Kit colors / style and category-aware corner logo composite",
      "Per-category Claude-calibrated prompts so each deliverable has a distinct visual treatment",
      "'Generate Image Package' buttons in Content Library, GBP Autopilot, and Brand Identity",
      "Per-deliverable regen, push to Content Library / Social Planner / GBP, favorites, version history",
      "Multi-package Compare view + Activity log with cost visibility + plan-tier monthly cap (default 30/mo)",
    ],
  },
  {
    version: "3.59.0",
    date: "2026-06-14",
    label: "Brand Kit — Creative & Publishing Agent foundation",
    tag: "feature",
    items: [
      "New 🎨 Brand Identity section in Business Brain — colors, fonts, voice, visual style, audience persona, preferred CTAs in one place",
      "✦ Auto-fill from your website — Claude proposes palette / fonts / voice / style in ~10 seconds, you review and save",
      "Niche-aware CTA suggestions calibrated to restaurants / healthcare / home services / retail",
      "Plan Dashboard: Brand Defaults per template + per-workspace override (super admin)",
      "Foundation for upcoming AI Creative Agent (Multi-Asset Image Package, Video Repurposing) — every future gen call inherits brand context",
    ],
  },
  {
    version: "3.58.0",
    date: "2026-06-13",
    label: "Index Agent — Reports rebuild (agency-grade)",
    tag: "feature",
    items: [
      "Reports tab rebuilt: period selector (7/30/60/90 days), 5 KPI tiles with vs-prior-period deltas, indexed-over-time chart, status distribution, page type breakdown, top issues",
      "🔗 Share report button generates a public read-only link (90-day expiry) that clients can open and print to PDF",
      "📊 Monthly report email — Resend-sent every 30 days to workspace owner, includes the shareable link",
      "All in Index Agent → Settings: toggle weekly digest and/or monthly report on/off",
    ],
  },
  {
    version: "3.57.0",
    date: "2026-06-13",
    label: "Index Agent — Impact / Attribution view",
    tag: "feature",
    items: [
      "New 📊 Index Agent Impact card on the Dashboard — shows newly indexed count + attribution rate for the last 30 days",
      "New 📈 Impact sub-tab with KPI tiles, attribution-by-method table, newly-indexed URL list, and a daily trend chart",
      "Attributes each newly-indexed URL to a specific Index Agent action (auto sitemap re-ping / manual sitemap / Indexing API) within a 14-day window, or 'organic' if Google found it on its own",
      "Period selector: 7 / 30 / 60 / 90 days · CSV export",
      "Quota saved metric: shows how many URL Inspection calls the GSC bulk sync prevented",
    ],
  },
  {
    version: "3.56.0",
    date: "2026-06-13",
    label: "Index Agent — Auto-actions v2 (notifications, Inbox card, auto AI, weekly digest)",
    tag: "feature",
    items: [
      "🔔 Bell notification when 1+ URLs drop out of Google index or become critical issues — one notification per day per workspace per event type",
      "📥 New 'Index Issue' card in your Inbox aggregating the top URLs needing attention; click 'Open Index Agent' to act",
      "🤖 Auto AI 'why isn't this indexed?' analysis fires automatically when a URL flips to a problem state — pre-fills the URL detail drawer's recommendation",
      "📧 Weekly email digest sent to workspace owners every 7 days — summary of indexed vs not-indexed, regressions, and top URLs needing attention",
      "All four routes are gated on signal — won't spam you when nothing changed",
      "Toggle digest on/off in Index Agent → Settings",
    ],
  },
  {
    version: "3.55.0",
    date: "2026-06-13",
    label: "Index Agent — Auto-actions v1 (sitemap re-ping on regressions)",
    tag: "feature",
    items: [
      "Index Agent now takes its first automated action: when the URL Inspection cron detects 3+ URLs that dropped out of the index OR 5+ stuck in 'Discovered, not crawled', it automatically resubmits your sitemap to Google",
      "24-hour cooldown per workspace — won't fire more than once a day, even if the trigger keeps happening",
      "New 🤖 Recent auto-actions card on the System tab shows every automatic submission with the reason it fired",
      "Submissions stored separately from manual requests so your 'Requested by me' filter stays clean",
    ],
  },
  {
    version: "3.54.0",
    date: "2026-06-12",
    label: "Index Agent — System tab (cron visibility + manual run)",
    tag: "feature",
    items: [
      "New 🔄 System sub-tab in Index Agent shows the behind-the-scenes work in plain English",
      "Cron schedule: 'URL Inspection · Hourly' and 'GSC Sync · Daily' with last/next run times",
      "Manual ▶ Run now buttons for both crons — skip the wait when testing",
      "Queue depth: how many URLs are due for re-check now and in the next 24h",
      "URL Inspection quota tracker: X / 2000 used today, color-coded as you approach Google's limit",
      "Run history table: last 15 cron runs with checked / succeeded / failed counts and duration",
      "Recheck cadence reference (Day 1 / 3 / 7 / 14 / 30 then pause)",
    ],
  },
  {
    version: "3.53.1",
    date: "2026-06-12",
    label: "Index Agent — Submissions view + filter",
    tag: "feature",
    items: [
      "New 📨 Submissions sub-tab in Index Agent shows every URL you've requested indexing on, sorted by most recent",
      "New Submission filter dropdown on the URLs sub-tab: 'All URLs / ↗ Requested by me / Not yet requested'",
      "Empty state for Submissions: friendly explanation when you haven't requested any URLs yet",
      "Backend: /api/index-agent/urls now accepts a submission query param to filter by last_submitted_at",
    ],
  },
  {
    version: "3.53.0",
    date: "2026-06-12",
    label: "SEO Rankings — AI suggest keywords + 50-keyword cap",
    tag: "feature",
    items: [
      "New '✦ Suggest 20 from website' button next to the Rankings keyword textarea — generates SEO keywords from your homepage + Business Brain via Claude Haiku",
      "Mix of head terms, service-specific, local intent, and long-tail informational keywords (20 total)",
      "Inline confirm before replacing existing keywords",
      "Bumped keyword cap from 20 to 50 — check rankings for many more keywords in one pass",
      "Updated placeholder text to match Health-style examples instead of stale demo content",
    ],
  },
  {
    version: "3.52.0",
    date: "2026-06-12",
    label: "Index Agent — multi-select + bulk actions on URLs",
    tag: "feature",
    items: [
      "Checkbox column with 'select all on page' in the header — pick any URLs to act on at once",
      "Sticky bulk action bar appears when you select URLs: Request Indexing, Re-check now, Ignore / Un-ignore, Set priority",
      "Smart enablement: 'Request Indexing' only acts on selected URLs that aren't already indexed; shows pre-flight counts",
      "Every row now has visible affordances: hover state, › chevron, and a ⋮ kebab menu with single-row quick actions",
      "Inline-confirm pattern for destructive bulk actions (Ignore) — no native browser dialogs",
    ],
  },
  {
    version: "3.51.1",
    date: "2026-06-11",
    label: "Index Agent — Help tab",
    tag: "feature",
    items: [
      "New ❓ Help sub-tab in Index Agent with 11 sections: Quickstart, How it works, Dashboard tiles, URL Status values, Priority levels, Recommended actions, Request Indexing, Recheck schedule, Submit URLs, Settings & caps, Troubleshooting",
      "Sticky TOC sidebar on desktop, dropdown selector on mobile (matches Lead Agent Help pattern)",
      "Plain-English explanations of every Dashboard tile and URL status — no SEO jargon required",
    ],
  },
  {
    version: "3.51.0",
    date: "2026-06-11",
    label: "Index Agent — Google Search Console bulk sync",
    tag: "feature",
    items: [
      "New 'Sync from Search Console' card in Index Agent → Settings — marks any URL that already gets impressions in Google Search as indexed",
      "Cheap: 1 API call returns up to 25,000 pages; no URL Inspection quota used for already-indexed URLs",
      "Auto-runs on every sitemap import + daily for every connected workspace",
      "Shows last-sync timestamp and 'Marked X URLs as indexed (of Y pages in Search Console)' result line",
      "Splits old 'Not Indexed' Dashboard tile into 'Not Indexed' + 'Pending Check' so freshly imported URLs no longer count as not-indexed before they've been checked",
    ],
  },
  {
    version: "3.50.0",
    date: "2026-06-11",
    label: "Plan Templates — explicit Product Shell",
    tag: "fix",
    items: [
      "Plan templates now have a 'Product Shell' setting: Full app or Content Agent dedicated shell",
      "Replaces the old heuristic that inferred Content Agent from module mix — broke once a second agent plan (SEO Agent) was defined with the same shape",
      "Existing Content Agent template + its workspaces auto-flip to the new field; everything else stays on the Full app shell",
      "Fixes: SEO Agent / other agent plans that include Content Agent module no longer render the Content Agent shell",
    ],
  },
  {
    version: "3.49.0",
    date: "2026-06-11",
    label: "Content Agent — Topic Mix control",
    tag: "feature",
    items: [
      "New Topic Mix section in Content Agent → Configure: balance Local Intent, Educational, Comparison, Seasonal topics across your plan",
      "Add custom categories with examples for the AI — rename, reorder, reweight, or delete",
      "Plan Dashboard: set a default mix per plan template that flows to every new workspace",
      "Current Mix panel shows actual vs target distribution with a one-click 'Rebalance my plan' action",
      "Existing workspaces get silently categorised on first Dashboard visit (~$0.001 one-time)",
    ],
  },
  {
    version: "3.48.0",
    date: "2026-06-10",
    label: "Content Agent Dashboard — Content Strategy Mindmap",
    tag: "feature",
    items: [
      "New Content Strategy Mindmap section on the Content Agent Dashboard tab",
      "Radial visualisation: workspace at the centre, pillar articles in a ring, supporting articles in outer arcs",
      "Click any node for a popover showing title, status, scheduled date, traffic, and SEO score",
      "Status drives the visual: published is solid, written is solid 85%, planned is dashed",
      "Cluster assignments come from Claude Haiku at plan generation — existing workspaces backfill lazily on first view",
      "Zoom controls (50–200%) for desktop; mobile shows a flat pillar list instead of the SVG",
    ],
  },
  {
    version: "3.47.0",
    date: "2026-06-06",
    label: "Index Agent — see which of your pages Google has indexed",
    tag: "feature",
    items: [
      "New SEO sub-tab: Index Agent — connect Google Search Console and see indexing status per URL",
      "Six sub-tabs: Dashboard / URLs / Submit / Issues / Reports / Settings",
      "Status per URL: Indexed, Discovered, Crawled but not indexed, Blocked by robots, 404, and more",
      "Request Indexing with a click — uses Google Indexing API for job pages, sitemap resubmit for all others",
      "Plain-English recommended action per page — no SEO jargon",
      "AI-powered detail in URL drawer: Haiku explains why a page isn't indexed and what to fix",
      "Imports URLs from sitemap (handles nested sitemap indexes), manual entry, CSV upload, and GSC backfill",
      "Automatic status checks on Day 1 / 3 / 7 / 14 / 30 schedule via hourly cron",
      "Per-workspace URL cap (default 500) configurable in Plan Dashboard and plan templates",
    ],
  },
  {
    version: "3.46.0",
    date: "2026-06-02",
    label: "Lead Agent Autopilot SP13 — Activity log",
    tag: "feature",
    items: [
      "New 📜 Activity tab in Autopilot mode — chronological log of every pipeline event",
      "Recent activity mini-feed on the Overview tab showing the last 5 events",
      "19 event types covering discovery / enrichment / scoring / auto-launch / drafts / replies / caps",
      "Filter sidebar: preset (all/errors/skips/user actions), event types (multi-select), date range, audience, lead search",
      "Per-row deep-links: View lead → / View audience →",
      "Persistent log (no auto-cleanup) — full audit trail per workspace",
    ],
  },
  {
    version: "3.45.0",
    date: "2026-06-02",
    label: "Lead Agent Autopilot SP12 — End-to-end autonomy",
    tag: "feature",
    items: [
      "Autopilot now auto-enriches discovered leads with websites — no Enrich button click needed",
      "Autopilot now auto-launches outreach when an enriched lead scores Hot or Warm — no Launch Outreach click needed",
      "New 'Require approval before sending' toggle in Overview — drafts queue in Inbox for one-click approval",
      "New ✉ Inbox card kind for approval-required drafts: Approve & Send / Discard / Open in Lead Agent",
      "Bug fixes: I1 false-saturation when cap exhausted, I3 AI scoring cap leak, I5 discovery cap race, I6 OOO snooze ignored",
      "SQL-atomic cap reservation across discovery / enrichment / outreach — concurrent crons no longer double-spend",
    ],
  },
  {
    version: "3.44.0",
    date: "2026-06-01",
    label: "Lead Agent Autopilot SP11 — Overview tab",
    tag: "feature",
    items: [
      "New 📊 Overview tab — first tab in Autopilot mode",
      "6 KPI tiles — Prospects Found, Qualified Leads, Active Campaigns, Replies Received, Appointments Booked (placeholder), Pipeline Value",
      "Score distribution chart — A/B/C/D quality + Hot/Warm/Cold/Frozen opportunity bars",
      "Reply category chart — all 8 categories, last 30 days",
      "Top-5 dynamic audiences table — status, cadence, lead count, last run",
      "❓ Help §16 explains every tile's formula and chart data source",
    ],
  },
  {
    version: "3.43.1",
    date: "2026-06-01",
    label: "Lead Agent Help Tab",
    tag: "feature",
    items: [
      "New ❓ Help tab in Lead Agent — quickstart + concept reference for every concept",
      "15 sections: Modes, Audiences, Score, Opportunity, Reply Categories, Take Over, and more",
      "Sticky TOC on desktop, dropdown selector on mobile",
      "Small ? icons next to LEAD SCORE / LEAD OPPORTUNITY / Audiences / Inbox lead_reply cards jump straight to the matching section",
    ],
  },
  {
    version: "3.43.0",
    date: "2026-06-01",
    label: "Lead Agent Autopilot SP9 — Reply Handling v2",
    tag: "feature",
    items: [
      "Inbound replies classify into 8 categories (was 4) — adds Pricing, Info, Appointment, Spam",
      "AI drafts a response per inbound reply — Business Brain + outreach context",
      "New side-panel composer: review, edit, regenerate, save, send",
      "Always-approve gate — drafts never auto-send",
      "🤝 Take over flips a lead into human_handoff so AI stops drafting; ↺ Resume AI brings it back",
      "Spam replies auto-mark the lead and hide it from default views",
      "Out-of-office replies snooze outbound outreach 14 days",
      "Scheduling URL (Calendly / Cal.com) — paste once, AI uses it in appointment-request drafts",
    ],
  },
  {
    version: "3.42.0",
    date: "2026-06-01",
    label: "Lead Agent Autopilot SP7 — Lead Quality + Opportunity scoring",
    tag: "feature",
    items: [
      "Two scores per lead: Lead Quality (A/B/C/D) and Opportunity (🔥 Hot / ☀ Warm / ❄ Cold / 🧊 Frozen)",
      "Recommended Action chip on every lead — routes into outreach, enrichment, manual review, or skip",
      "Email gating: leads without an email cap at C tier (since outreach is email-only today)",
      "Score Breakdown panel in the lead detail drawer shows each factor with bars and the AI rationale",
      "New SCORE filter section in the sidebar — click A/B/C/D or Hot/Warm/Cold/Frozen to filter the table",
      "↺ Re-score All in the My Leads header — recomputes every lead with AI review on borderline cases",
      "Auto-rescores when leads are discovered, enriched, replied to, or when you edit an audience's ICP",
    ],
  },
  {
    version: "3.41.0",
    date: "2026-05-31",
    label: "Lead Agent Autopilot SP6 — ICP + Audiences",
    tag: "feature",
    items: [
      "Manual ↔ Autopilot mode toggle inside Lead Agent (existing workspaces land in Manual; new workspaces default to Autopilot)",
      "New Audiences view replaces the keyword Search Agents panel; sections for Dynamic / Saved / Campaign",
      "AI ICP Builder: describe your ideal customer in plain English, Claude Haiku populates a structured ICP form",
      "9-niche curated catalog (Restaurants, Med Spas, Vein Clinics, Dental, Plumbers, Landscapers, HVAC, Tile, Manufacturing) + Custom free-text",
      "Discovery runs continuously per Dynamic Audience (hourly or daily), filtered by rating range, review count range, website-required, and more",
      "Existing search agents auto-migrate to Dynamic Audiences on first load — no leads lost, hourly discovery uninterrupted",
    ],
  },
  {
    version: "3.40.2",
    date: "2026-05-30",
    label: "Lead Agent — manual entry + CSV import",
    tag: "feature",
    items: [
      "Inline-edit Email, Phone, Contact name, Contact title in the lead detail drawer.",
      "+ New Lead button in My Leads header opens a modal with Business name (required) plus Website / Category / City / Phone / Contact section.",
      "⬆ Import CSV button opens a 3-step wizard (Upload → Map → Verify) mirroring the CRM CSV pattern; auto-detects column mappings, clamps to remaining monthly cap, surfaces skipped counts.",
      "Outreach gate flipped: any lead with an email can launch outreach. Enrichment becomes optional, not required.",
      "Server-side: /api/lead-agent/save now preserves email / contactName / contactTitle fields from input (was silently stripping to null).",
      "Source badges added: 📝 Manual and ⬆ CSV.",
    ],
  },
  {
    version: "3.40.1",
    date: "2026-05-30",
    label: "Lead Agent SP5 — Outreach reply conversion",
    tag: "feature",
    items: [
      "Outreach emails now carry a Reply-To alias (reply+<leadId>@app.getkurb.ai, or @<workspace-verified-domain> when set).",
      "New endpoint POST /api/lead-agent/inbound-reply receives Resend Inbound webhooks (HMAC SHA-256 signature verified).",
      "Claude Haiku classifies each reply as positive / not_interested / ooo / other (~$0.001 per reply).",
      "Positive or other: stops the outreach sequence, creates a CRM Company linked to the existing Contact, advances the Conversion lead, surfaces an Inbox card.",
      "Not interested: stops the sequence, advances Conversion lead to lost, no Inbox card.",
      "OOO / autoresponder: logged on the lead only, sequence keeps going.",
      "Outreach panel gains a Replies section with category badges. Lead Agent sidebar gains a Replied view.",
    ],
  },
  {
    version: "3.40.0",
    date: "2026-05-30",
    label: "Plan Dashboard — Lead Agent caps",
    tag: "feature",
    items: [
      "Three caps per plan template and per workspace: Saved leads / mo, Enrichments / mo, Outreach launches / mo (blank = unlimited).",
      "Plan template editor exposes the three fields. Workspace drawer shows used / cap per row with inline Save.",
      "Workspaces admin table gets a Leads column (used / cap).",
      "Outreach launches now enforce lead_agent_outreach_cap; HTTP 429 surfaces a clear cap-reached error.",
      "Reset helper zeroes all three used counters together once a workspace's lead_agent_month_reset_at is >30 days old. Wired into save, visitor-promote, runDueEnrichments, and launch-outreach.",
      "apply-domain-template now copies the three caps from the plan template into the new workspace at signup.",
    ],
  },
  {
    version: "3.39.0",
    date: "2026-05-28",
    label: "Call Tracking — Caller Enrichment (Name + City via Twilio Lookup)",
    tag: "feature",
    items: [
      "Every inbound call now gets a name lookup via Twilio Lookup v2 (caller_name + line type intelligence) when the caller isn't already in your CRM",
      "Missed calls and unknown callers automatically get a city from area-code lookup (~280 US area codes supported)",
      "Names looked up from carrier database show a small 📞 badge next to the name — distinguishes from your CRM-matched contacts",
      "Phone Info card in the activity detail panel shows mobile / landline / VoIP type from carrier",
      "First lookup that returns a name auto-creates a CRM contact, linking the activity",
      "Per-workspace monthly cap (default 1,000 lookups / ~$15) — admin-overridable in Plan Dashboard",
      "Manual re-lookup endpoint ready for future ↺ Re-lookup button",
      "Activity trend chart now collapsible with a Hide chart / Show chart pill — preference persists across sessions",
    ],
  },
  {
    version: "3.38.0",
    date: "2026-05-27",
    label: "Call Tracking Activity — Trend Chart + Filter Chips",
    tag: "feature",
    items: [
      "Activity tab now shows a multi-line trend chart at the top of every sub-view — grouped by tracking number by default, switchable to Source or AI Classification",
      "Date range filter at the top of Activity (default 7d) with presets Today / 7d / 30d / 90d / Month / Custom",
      "+ Add filter chip system — narrow by Source / Tracking Number / AI Classification / Lead Score ≥ 7; chips dismiss with ⊗",
      "Counter line shows '{N} Calls from May 19 – May 26' style summary, adapts per sub-view (Calls / Forms / Unique Callers / etc.)",
      "Chart auto-buckets by hour / day / week based on the date range",
      "Top 7 series rendered; rest collapsed into 'Other' line",
      "Existing search box still works — narrows the list only, chart stays steady as a visual anchor",
    ],
  },
  {
    version: "3.37.0",
    date: "2026-05-27",
    label: "Call Tracking Reports — Volume, Keywords, Conversion Rate",
    tag: "feature",
    items: [
      "New report: Call Volume Over Time — stacked bar chart (calls + forms) with missed-call cap overlay, auto-bucketed by range (hour/day/week)",
      "New report: Top Keywords — two sub-tabs (Paid Keywords from utm_term/gclid, Conversation Topics from AI-extracted transcript keywords)",
      "New report: Conversion Rate by Channel — 3-step funnel (Touches → Qualified → Won) with per-row 'Open in Conversion ROI' deep link",
      "Date range filter at the top of every report — presets (Today / 7d / 30d / 90d / Month / Custom) applies to all 10 reports; default 30d",
      "AI keyword extraction now runs as part of the existing recording-webhook Haiku call — no extra API cost, always fresh",
      "Open in Conversion ROI jumps straight to Conversion → Metrics with the channel pill and date range pre-applied",
    ],
  },
  {
    version: "3.36.0",
    date: "2026-05-24",
    label: "Conversion Attribution — Channel-level ROI",
    tag: "feature",
    items: [
      "Every lead now captures first-touch + last-touch UTMs (utm_source, utm_medium, utm_campaign, utm_term, utm_content)",
      "Metrics tab → new Channel ROI table with Leads / Won / Revenue / Spend / Cost-per-Won / ROAS columns",
      "Attribution toggle: switch between Last Touch and First Touch views without re-importing data",
      "Custom date range filter alongside Month / 30 / 90",
      "New Settings → Marketing Spend matrix: enter manual spend per channel per month, with API-pulled values from Google Ads taking priority when both exist",
      "Google Ads connection now auto-syncs spend every 6 hours (when OAuth connected and Customer ID set)",
      "Channel name mapping editor — define how raw UTM source values (e.g. 'tiktok') map to display names (e.g. 'TikTok Ads')",
      "Per-lead manual channel override — tag walk-in / referral / phone-call leads that don't have UTMs",
      "Contact form webhook now accepts utm_* fields; ct.js exposes window.kurbai.getUtm() for sites to spread into form payloads",
      "Lazy backfill — historical leads get their UTMs populated from linked Call Tracking sessions as you browse the funnel",
    ],
  },
  {
    version: "3.35.0",
    date: "2026-05-24",
    label: "GBP Autopilot — Custom Schedule + Pluggable Image Sources",
    tag: "feature",
    items: [
      "Settings → custom day picker (pick any days including weekends) + custom posting hour selector",
      "Pluggable image sources with priority chain: Business Brain → Media Library → Stock photos (Pexels) → AI Generate, drag to reorder",
      "Pexels stock photos integration — free professional photos when Brain has no match (requires PEXELS_API_KEY env var)",
      "Calendar chips now show 48px image thumbnail + spelled-out status label (Scheduled/Planned/Published/etc) + status-colored border",
      "⊕ Replace image button in post detail drawer — pick from Media Library, paste URL, or generate a custom AI image",
      "Regenerate content now preserves your manually-picked image (was overwriting it)",
      "Regenerate content keeps the drawer open (no longer closes the side panel)",
      "Calendar refreshes silently after edits (no more loading flicker)",
      "Bug fix: generate-plan no longer infinite-loops when starting on a weekend with cadence=1",
      "Bug fix: drawer reflects fresh data after image replace / content regenerate (was showing stale post)",
    ],
  },
  {
    version: "3.34.0",
    date: "2026-05-23",
    label: "Google Business Profile — Autopilot",
    tag: "feature",
    items: [
      "New ⚡ Autopilot tab in Google Business Profile module — scheduled AI-generated posts on a 1–3× per week cadence",
      "4-step setup wizard: Connect Google → Configure cadence → Preview 12-week plan → Activate",
      "Connect Google Business Profile via OAuth (merged with existing Google Ads OAuth — one consent screen, both products)",
      "Calendar + List views, drag-to-reschedule, post detail drawer with regenerate / retry / skip",
      "Hybrid publish: real Google API for partner-approved workspaces, Inbox queue with one-click manual publish for everyone else",
      "Brain-photo first image sourcing with AI-generated fallback when no relevant photo exists",
      "Approval-before-publish toggle (default ON) — posts queue to Inbox for one-tap approve",
      "Prompt Customization (3-tier): super admin per plan + per workspace + workspace-owner override",
      "Cron auto-expires posts awaiting approval >7 days; max 10 posts/day per workspace",
    ],
  },
  {
    version: "3.33.0",
    date: "2026-05-23",
    label: "Conversion ↔ CRM — Unified Pipeline",
    tag: "feature",
    items: [
      "Every Conversion lead now automatically creates a CRM contact (deduped by email/phone) — CRM is always the permanent record",
      "Every CRM contact (scan card, CSV import, manual, contact form) now automatically creates a Conversion pipeline lead — pipeline view stays complete",
      "New 'Pipeline' section on every CRM contact panel showing linked Conversion leads with status, qualification, source, and Won $ value",
      "'View in CRM' from Conversion lead panel deep-links to the contact's CRM record",
      "'Open in Conversion →' from CRM Pipeline section deep-links to the lead's panel (auto-switches to All Leads if hidden by Hot Leads filter)",
      "Contact form webhook now also enters the Conversion funnel — AI deep-qualifies in the background using the form message",
      "Lazy backfill: opening any older CRM contact automatically creates their missing pipeline entry on first view",
      "Performance: CRM mount went from 10 round-trips to 1 batched query, plus session-level cache for instant repeat-visits",
      "Bug fix: Conversion module's metrics + lead actions are no longer broken in super-admin 'viewing as workspace' mode (wsId was reading the wrong profile field)",
      "Bug fix: Lead status dropdown now updates instantly with optimistic UI — silent PATCH failures surface as errors instead of doing nothing",
    ],
  },
  {
    version: "3.32.0",
    date: "2026-05-22",
    label: "Content Agent — Image Replace + Link Polish",
    tag: "feature",
    items: [
      "Featured Image card now has a ⊕ Replace button alongside ↺ Regenerate — pick from Media Library, upload, paste URL, or AI-generate with a custom prompt",
      "Internal links inside articles now render as clickable blue hyperlinks in Preview and as real <a href> tags in Edit mode source view",
      "Internal Linking sync now reports per-source errors (WordPress + sitemap) with ⚠ icons and an expandable detail panel — no more silent 0-result mysteries",
      "Setup wizard now works on sites protected by Wordfence/Sucuri/Cloudflare: added a user-agent fallback chain (Chrome → Googlebot → KurbAI bot) so cloud-egress IPs aren't blocked",
      "Clearer error message when a site's security plugin blocks our server — tells the user what's happening and what to try",
    ],
  },
  {
    version: "3.31.0",
    date: "2026-05-21",
    label: "Content Agent — Internal Linking",
    tag: "feature",
    items: [
      "Articles now include internal links to your site's other pages — AI was previously refusing to invent URLs",
      "New 'Internal Linking' section in Content Agent Configure tab",
      "3 sources merged into one inventory: WordPress (auto-pulled when connected), sitemap.xml URL, and manual page list",
      "Manual pages format: one per line, 'Label | URL' — useful for highlighting hero pages",
      "Sync now button pulls fresh inventory; counts shown per source plus last-synced timestamp",
      "Article generation injects up to 25 most relevant pages per article (ranked by keyword overlap)",
      "Dedup priority: manual > WordPress > sitemap (so user labels win over auto-derived ones)",
    ],
  },
  {
    version: "3.30.0",
    date: "2026-05-20",
    label: "Call Tracking — Full Launch",
    tag: "feature",
    items: [
      "New module: Call Tracking — attribute every inbound call and form submission to its marketing source",
      "ct.js snippet: DNI pool swap (sessionStorage session cache, MutationObserver re-swap, boundary-safe +1 prefix handling), form capture, page view beacon",
      "Session Journey: every call detail panel shows the visitor's full page-by-page browse path before calling",
      "Live Sessions panel in Tracking Numbers: see active pool sessions in real time (30s auto-refresh, browser/device, landing page)",
      "AI classification on every activity: new lead / existing customer / spam with lead score 0–10 (spam capped ≤2)",
      "CRM caller matching: inbound calls resolve caller name from existing CRM contacts by phone number",
      "Third-party form webhooks: GoHighLevel and Zapier/Make/Typeform/Jotform/Facebook Lead Ads",
      "Pool source channel: default changed to 'Website', inline ✎ edit on pool panel (was incorrectly 'Google Ads')",
      "Universal ↺ Refresh button added to all modules in the app shell",
      "Content Agent: full-width layout fix, Prompt Customization section in Configure tab",
      "Settings Team & Roles: super admin now sees correct client workspace team (server-side RLS bypass)",
    ],
  },
  {
    version: "3.29.0",
    date: "2026-05-18",
    label: "Lead Agent — Outreach (Sub-project 4)",
    tag: "feature",
    items: [
      "✦ Start Outreach → button enabled on enriched leads with email — opens outreach panel in detail pane",
      "Configure sequence: 2 or 3 emails, tone (Friendly/Professional/Direct), custom day spacing per email",
      "Claude generates all emails at once using enriched contact, LinkedIn, website, and GBP data",
      "Review + edit each email inline before launching — subject and body both editable",
      "Email 1 sends immediately on launch via Resend; follow-ups auto-send via Railway cron every 5 minutes",
      "Outreach tab enabled — shows active and completed sequences with per-lead progress chips",
      "Outreach status chips (⏳ Outreach / ✓ Outreach) appear in My Leads Enrichment column",
      "Lead status automatically set to 'contacted' on launch",
    ],
  },
  {
    version: "3.28.0",
    date: "2026-05-17",
    label: "Lead Agent — Lists, Tags & Sidebar Organization",
    tag: "feature",
    items: [
      "Left sidebar in My Leads — Views (built-in smart filters), My Lists, Tags, and Saved Views sections",
      "Named lists — create color-coded lists, leads can belong to multiple lists, assign at save time",
      "Free-form tags — apply tags inline per lead row or bulk via checkbox selection",
      "Sidebar filter replaces status dropdown — click any view/list/tag to instantly filter the table",
      "Saved views — name and save any sidebar filter combo for one-click return",
      "Lists / Tags column in My Leads table with inline + list dropdown and + tag input per row",
      "Bulk actions — Add to List and Add Tag added to the bulk selection bar",
      "Split button on Save Lead and Save All in Search — assign to a list at save time",
    ],
  },
  {
    version: "3.27.0",
    date: "2026-05-17",
    label: "Lead Agent — Lead Enrichment (Sub-project 3)",
    tag: "feature",
    items: [
      "Lead enrichment — manual per-lead '✦ Enrich' button or batch checkbox select with 'Enrich Selected (N)'",
      "Apollo.io integration — finds key contact name, title, email, and LinkedIn URL by company domain",
      "Apify LinkedIn Company Scraper — pulls company about, employee count, and latest post for outreach hooks",
      "Website intelligence — scrapes + summarises lead's website via Claude Haiku for personalized context",
      "GBP description — pulls Google Business Profile description for GBP Search leads via DataForSEO",
      "Async background queue — enrichment runs on Railway every 2 min; leads show 'Queued → Enriching… → Enriched' status",
      "Monthly enrichment cap — admin-controlled per plan; confirmation dialog shows credits to be used before committing",
      "Enriched lead detail panel — Key Contact, LinkedIn summary, website intelligence, GBP description sections",
    ],
  },
  {
    version: "3.26.0",
    date: "2026-05-17",
    label: "Lead Agent — Website Visitor De-anonymization (Sub-project 2)",
    tag: "feature",
    items: [
      "JS tracking snippet (GET /track.js) — embed one script tag on any website to start capturing visitors; works on traditional sites and SPAs (intercepts pushState/popstate)",
      "Two-tier IP classification — free ASN check filters ~70% residential traffic (AT&T, Comcast, etc.) before calling IPinfo.io Business; 24h global IP cache minimises API spend",
      "Company rollup — all sessions from the same domain grouped into one company record; cross-session page counts (e.g. /pricing ×3 ★) reveal repeat intent",
      "Visitors tab in Lead Agent — company feed sorted by last seen; 🔥 returning badge for 2+ visits, ⚡ high-intent badge for pricing/contact/menu pages; detail panel with full session history + per-page dwell times",
      "→ Save Lead — one-click promotes a visitor company into My Leads with source: website_visitor",
      "Notifications — bell fires when a known company returns, or hits a high-intent page for the first time",
      "Setup Tracking modal — copy-paste snippet with one-click copy; auto-install button for connected CMS (WordPress/Wix/Squarespace)",
    ],
  },
  {
    version: "3.25.0",
    date: "2026-05-16",
    label: "Lead Agent — GBP Lead Scraper (Sub-project 1)",
    tag: "feature",
    items: [
      "New module: 🎯 Lead Agent — find, save, and track leads from Google Business Profiles",
      "On-demand search: keyword + city + radius → DataForSEO Maps API → 2-column results grid with Save Lead / Save All bulk action",
      "Search Agents: autopilot agents run hourly, auto-pause when market is exhausted (saturation detection after 3 consecutive empty runs), weekly check-in to catch newly opened businesses",
      "Lead caps: workspace monthly cap (plan-based, admin-controlled) shown as progress bar above tabs; per-agent cap with inline input + progress bar; agents pause automatically when either cap is reached",
      "My Leads list: filter by name/city and status (New / Enriched / Contacted), detail slide-in panel, push to CRM as Company, inline delete confirmation",
      "Bell notification when search agents find new leads: '🎯 N new leads found — [keyword] in [city]'",
    ],
  },
  {
    version: "3.24.0",
    date: "2026-05-16",
    label: "CRM — In-Browser Web Calling with AI Transcription",
    tag: "feature",
    items: [
      "Web calling — click '📞 Call' on any contact to open a dial pad pre-filled with their number (auto-normalized to E.164); call entirely in-browser via Twilio Voice SDK (no physical phone needed)",
      "4-state call panel: dial pad → dialing animation → active call (mute / hold / speaker / DTMF keypad / live notes) → post-call log with outcome picker",
      "Recording consent disclosure played automatically at call connect: 'This call may be recorded for quality purposes'",
      "AI transcription — call is recorded, transcribed by OpenAI Whisper, then summarised by Claude in 2–3 plain sentences with action items",
      "AI Summary and full transcript saved to contact activity timeline — collapsible transcript section per call entry",
      "Mobile: tapping Call or Send SMS auto-switches to Activity tab so the panel is always visible",
      "CRM contacts table — horizontal scroll enabled so all columns are reachable on narrow screens",
    ],
  },
  {
    version: "3.23.0",
    date: "2026-05-16",
    label: "CRM — SMS Inbound Notifications & Contact Form Webhook",
    tag: "feature",
    items: [
      "SMS inbound notifications — when a customer texts your Twilio business number, the contact is auto-created in CRM and your agent receives an instant SMS alert on their cell phone",
      "Contact form webhook — publish a single webhook URL to your website; contact form submissions auto-create CRM contacts and trigger an SMS notification to your team",
      "Settings → Notifications: new CRM Inbound Alerts section — save your agent phone number, see the Twilio inbound webhook URL, copy the contact form webhook URL, send a test SMS",
      "Duplicate detection on inbound SMS and contact form — matches by phone or email before creating a new contact",
      "Inbound SMS logged in CRM activity timeline with direction: inbound indicator",
    ],
  },
  {
    version: "3.22.0",
    date: "2026-05-16",
    label: "CRM — Kanban Board, Business Card Scan, Import Wizard & Mobile",
    tag: "feature",
    items: [
      "Multi-step CSV import wizard — Upload (drag-drop + mode selector + sample file download) → Map (auto-detects column names, shows sample values, field dropdown per column) → Verify (mapping summary + consent checkbox)",
      "Business card scan — 📷 Scan Card button opens live camera view with card guide frame; corner markers turn green when card is detected; Claude Vision extracts all fields; unknown fields (fax, job title, LinkedIn, etc.) auto-create custom properties on first scan",
      "Board View redesign — HubSpot-style kanban: colored column headers per lifecycle stage, elevated cards with left accent bar and drag grip (⠿), card lift animation on hover, glow effect on drag-over, 'Drag cards between columns' hint bar",
      "Export CSV — exports current filtered/sorted view with all contact fields including custom properties",
      "✦ Draft Email with AI — generates a personalized email subject + body from contact context, pre-fills Email Campaigns composer without a second AI call",
      "Add to Email List — bulk action that upserts selected contacts as email subscribers and adds them to any named list",
      "Escape key closes slide-in contact or company panel from anywhere on the page",
      "Mobile — CRM header buttons wrap correctly; views pills stack above contact list; search bar condenses to icon-only toggle on mobile",
      "Fix: custom properties saved by business card scan and CSV import now use fieldKey as the store key (was using prop.id — values were invisible in the contact panel)",
      "Mobile app-wide — hamburger ☰ menu was never wired (sidebar unreachable on mobile); fixed for both main and DefaultShell; TopBar adapts on mobile; iOS input zoom prevented (font-size 16px on mobile); overflow-x hidden on body",
      "Auth screen — mobile layout: left brand panel hidden, compact logo header shown, form centered with equal 24px padding",
    ],
  },
  {
    version: "3.21.0",
    date: "2026-05-15",
    label: "CRM — HubSpot-style Contacts & Companies",
    tag: "feature",
    items: [
      "Contacts redesigned with a saved views sidebar (All Contacts, Leads, Customers, VIPs, Recently Added) — build custom views with type / source / tag / date filters",
      "Sortable table with Primary Company, Lead Status, Tags, Source, Last Activity columns — toggle any column on or off with the new Columns picker",
      "New contact form captures First Name and Last Name separately",
      "Click any contact to open a 720px slide-in panel: inline-editable properties (click any field to edit), tags with autocomplete, company link, Lead Status dropdown",
      "Activity timeline in the contact panel: log notes (Enter to save), log calls with outcome, log SMS — plus reservations and Google reviews auto-surfaced from other modules",
      "✦ AI suggest follow-up on any logged note",
      "Companies tab — track companies with their own sortable list and slide-in panel",
      "Company panel: properties, linked contacts list with search-to-associate; click a contact from the company panel to navigate with a back breadcrumb",
      "Bulk actions: add tag, change type, or delete multiple contacts at once",
      "Kurb AI now knows contact counts by type, all tags in use, stale leads, and company count",
      "CRM module goes full width — uses all available screen space, responsive when the AI assistant panel is open",
    ],
  },
  {
    version: "3.20.0",
    date: "2026-05-15",
    label: "Admin Shells — Super Admin & Agency Admin",
    tag: "feature",
    items: [
      "Super admins get a dedicated sidebar shell: 'Super Admin' badge, Plan Dashboard, and Client Workspaces — no workspace switcher",
      "Agency admins get their own sidebar shell: 'Agency Admin' badge and Client Workspaces showing only their clients",
      "Enter any client workspace with one click — persistent '← Exit to Admin' banner lets you return instantly",
      "Client Workspaces list has live search and pagination (20 / 50 / 100 per page)",
      "Plan Dashboard workspace rows now have an 'Enter →' button for quick access",
      "Plan Dashboard → workspace General tab shows the workspace owner's email and an Agency Admin toggle",
      "New server endpoint POST /api/admin/update-profile for admin profile mutations",
      "Agency owners are automatically granted owner access to new workspaces created under their subdomain",
      "Fix: Articles Live count on Content Agent dashboard now matches the Articles tab (reads from library, not calendar slots)",
    ],
  },
  {
    version: "3.19.0",
    date: "2026-05-13",
    label: "Content Agent — Add Website",
    tag: "feature",
    items: [
      "Content Agent users can now add a second website — 'Add Another Website' option in the CA sidebar and workspace picker",
      "Pricing modal: $29/month or $24/month billed annually per additional site",
      "Purchase intent is recorded and ready for Stripe activation — no manual back-and-forth needed",
      "Media Library is now accessible from the Content Agent sidebar",
      "MiniEditor: Insert Media button opens your media library and inserts the selected image at cursor position",
    ],
  },
  {
    version: "3.18.0",
    date: "2026-05-13",
    label: "Content Agent — Articles & WordPress Publish",
    tag: "improvement",
    items: [
      "New Scheduled tab — shows all articles queued in the autopilot calendar pipeline",
      "New Unpublished tab — distinct from drafts; tracks articles removed from CMS or that failed to publish",
      "Unschedule and Delete actions available directly from the Articles list",
      "Unpublish: reverts the WordPress post to draft and resets the article's local status",
      "Article Info panel now shows the scheduled date and published date",
      "Sidebar navigation now works correctly when you're inside an article detail view",
      "Featured image is automatically uploaded to the WordPress Media Library when publishing",
      "Category is auto-selected using AI — no manual category selection needed",
      "Markdown content is properly converted to HTML before sending to WordPress (bold, headings, lists now render correctly)",
    ],
  },
  {
    version: "3.17.0",
    date: "2026-05-13",
    label: "Content Agent — Dedicated Dashboard & Navigation",
    tag: "improvement",
    items: [
      "Content Agent plan users now get a purpose-built experience — flat sidebar nav replacing the full platform sidebar",
      "Navigation: Overview, Calendar, Keywords, Articles, Brand Guide, Configure, Media",
      "New Home screen: personalised greeting, auto-publish toggle, and a live list of upcoming scheduled articles",
      "Brand Guide page: review and edit your business profile (name, niche, tone, USP) directly inside Content Agent",
      "Setup wizard now streams live progress — each analysis step shows status as it completes",
      "Business profile analysis is saved to Business Brain automatically during setup",
      "Settings and Integrations views are tailored to Content Agent (no irrelevant platform options)",
      "Image generation updated to Imagen 4 + Gemini 2.5 Flash; text overlays removed from image prompts",
      "Image quality setting is now applied consistently across article generation, regeneration, and editing",
    ],
  },
  {
    version: "3.15.0",
    date: "2026-05-11",
    label: "Agent Domain Launch",
    tag: "feature",
    items: [
      "Content Agent is now available as a standalone product at ca.getkurb.ai",
      "New users signing up on an agent subdomain get a pre-configured workspace with only the relevant modules",
      "Content Agent plan includes: Dashboard, Content Agent, Business Brain, Media Library, Integrations, Settings",
      "After onboarding, agent-plan users land directly in their agent module — not the full platform dashboard",
      "Pattern is reusable: any future agent (Review Agent, Social Agent) needs only a plan template + subdomain config",
    ],
  },
  {
    version: "3.14.0",
    date: "2026-05-11",
    label: "Content Agent — Keywords & CMS Connection",
    tag: "feature",
    items: [
      "Keywords tab now shows every article in your calendar with publish date, difficulty, search volume, and status",
      "Auto-discover new keywords daily — Claude Haiku finds 8 fresh keyword ideas each day, avoiding duplicates",
      "Add keywords to your calendar directly from the Keywords tab, or dismiss ones you don't want",
      "CMS auto-detection — your website platform (WordPress, Wix, or Squarespace) is identified automatically from your site's HTML",
      "Setup wizard Step 4 shows a 'Detected: WordPress' banner when your platform is recognised",
      "Connect your CMS directly in Content Engine Settings — no need to visit the Integrations module",
      "WordPress: enter site URL, username, and application password with a live Test Connection check",
      "Wix: enter Site ID and API key with guided links to where each is found in Wix dashboard",
      "Squarespace: enter API key with guided link to Squarespace Advanced settings",
      "CMS credentials sync bidirectionally — connecting from Content Engine also updates the Integrations module",
    ],
  },
  {
    version: "3.13.0",
    date: "2026-05-11",
    label: "Content Agent",
    tag: "feature",
    items: [
      "New Content Agent module — full autopilot blog content pipeline",
      "4-step setup wizard: website analysis → brand review → upcoming articles → go live",
      "Month calendar view with lazy article generation (writes 1 day before publish)",
      "Article detail page with SEO score gauge, editable slug/meta, featured image",
      "Generate Now button: trigger any planned article on demand",
      "WordPress publish live · Wix + Squarespace integrations added",
      "Daily Railway cron: auto-generates and publishes articles on schedule",
      "Approve-before-publish mode: articles land in Inbox for review first",
    ],
  },
  {
    version: "3.12.0",
    date: "2026-05-10",
    label: "Business Brain — Intelligence Hub",
    tag: "feature",
    items: [
      "Business Brain redesigned from data asset store to intelligence hub — answers 4 questions every business owner has",
      "Section 1: 'How is my business doing?' — AI-generated narrative + 5 plain-English status signals (customer activity, visibility, reviews, marketing, pending actions)",
      "Section 2: 'What needs attention?' — 🔴 urgent attention cards grounded in real data; each card explains what's happening, why it matters, and expands in-place with an AI-drafted action",
      "Section 3: 'What opportunities exist?' — 🟡 opportunity and 🟢 momentum cards for things worth acting on (upcoming dates, engagement wins, campaign gaps)",
      "Section 4: 'What should I do next?' — AI-prioritised ranked recommendations with one-click action drafting; manual ↺ re-rank always available",
      "In-place action drafting: review reply drafts, social post drafts, email campaign drafts, campaign briefs, GBP posts — all without leaving Business Brain",
      "Business Assets (menu, team, photos, events, social proof, website) preserved in collapsible accordion at bottom",
      "Single batched Claude call generates all 4 sections; cached 24h (health) / 7d (insights); manual ↺ Refresh always available",
    ],
  },
  {
    version: "3.11.0",
    date: "2026-05-09",
    label: "Email Campaigns — Full Inline Editor",
    tag: "feature",
    items: [
      "EmailInlineEditor: full enhanced toolbar with undo/redo (15-step history), duplicate block, duplicate section, delete block, move up/down",
      "Text colour picker — 30-swatch palette + custom colour input; selection preserved when picker opens",
      "Link editor — appears automatically when editing an <a> element; inline URL field with Apply/Cancel",
      "Padding presets — S (snug), M (normal), L (roomy) applied to the active cell",
      "Section hover highlight — subtle dark outline appears on row hover without clicking",
      "Section pill toolbar — dark floating pill on hover with drag-to-reorder handle, move up/down, duplicate, YouTube URL setter, delete",
      "Drag-and-drop — drag any element or layout tile from the left panel into the email template; green drop indicator line shows insertion point",
      "YouTube panel — paste a YouTube URL to auto-set video thumbnail from img.youtube.com; click plays in email client",
      "Broken image placeholders — offline/missing images show a styled 'Click to replace' SVG instead of broken icon",
      "All scripts use tryAdd() polling pattern for reliable injection order regardless of iframe load timing",
    ],
  },
  {
    version: "3.10.0",
    date: "2026-05-07",
    label: "Inbox — Centralised Approval Hub",
    tag: "feature",
    items: [
      "New Inbox module — virtual aggregator that surfaces all pending items from every module in one place",
      "Aggregates: Social Planner pending/approved posts, Ads Manager pending campaigns + Quick Ads drafts, AI-drafted review replies, Content Writer drafts, Guest Marketing campaigns",
      "Two-panel desktop layout: scrollable feed on the left, full detail view on the right",
      "Four tabs: To Review (live count badge), Approved, Declined, Done",
      "Filter pills: All / Social / Ads / Reviews / Content / Email/SMS",
      "Deep-link navigation: 'Open in [Module] →' fires typed events so the destination module jumps directly to the specific item — no hunting",
      "✓ Approve: updates Social posts and Ad campaigns to 'approved' status directly from Inbox without leaving it",
      "✕ Decline: moves item to Declined tab, persisted to inbox-declined store with timestamp",
      "↩ Restore: moves declined items back to To Review",
      "Urgency signals: red dot on items scheduled within 24h or review replies for ≤3★ reviews",
      "Dismiss: hides items from Inbox permanently without touching the source record",
      "Auto-refreshes on ads-updated, social-updated, reviews-updated events",
      "Mobile: stacked layout with ← Back navigation between list and detail",
      "Inbox added as second sidebar item (after Dashboard) with 📥 icon",
    ],
  },
  {
    version: "3.9.0",
    date: "2026-05-07",
    label: "Ads Manager — Quick Ads & Google API",
    tag: "feature",
    items: [
      "⚡ Quick Ads tab — one-click ad generator: pick a goal (6 types), optional context, daily budget ($5–$50 or custom), run duration (7/14/30/60 days or custom date range)",
      "AI generates complete Google ad copy (5 headlines + 4 descriptions) and Meta copy (primary text + headline + image guidance) using Business Brain data",
      "Results show live platform-tabbed previews: Google Search mockup, Meta Feed card, Instagram Story — all using real ad preview components",
      "Auto-generates creative image via /api/generate-image (Pollinations → Supabase Storage); shimmer placeholder while generating; ↺ New image with random seed",
      "Collapsible sections: Ad Setup / Ad Preview / Ad Copy — form auto-collapses after generation showing summary line",
      "💾 Save Ad button saves directly to My Campaigns as a draft campaign with all copy + budget + dates pre-filled",
      "⚡ Quick Ads shortcut button added to module header alongside + Create Campaign",
      "Google Ads API scaffolded — server endpoints: OAuth2 start/callback, connection status, campaign publish (Budget → Campaign → Ad Group → RSA → Keywords → Sitelinks → Callouts → Call Asset), 30-day stats sync via GAQL, pause/enable",
      "Settings tab: Connect with Google Ads OAuth button, workspaceId-scoped token storage in Supabase, success/error banner on redirect-back",
      "Campaign Type Launcher: 9 types across Google (Search, Display, YouTube, LSA, PMax) and Meta (Feed & Stories, Carousel, Video, Lead Gen) — available vs coming-soon states",
      "Wizard: fully separated Google/Meta platform tabs in Steps 2 (targeting) and 3 (copy); googleCopy/metaCopy/googleTargeting/metaTargeting data model",
      "Review step (Step 4): separate Google Ads and Meta Ads summary panels with platform-specific fields",
    ],
  },
  {
    version: "3.8.0",
    date: "2026-05-07",
    label: "Ads Manager — Platform Separation & Settings",
    tag: "feature",
    items: [
      "Wizard Steps 2 and 3 now have separate Google / Meta tabs — no more mixed forms",
      "Google targeting: audience description + keyword list; Meta targeting: audience, interests, age range, gender, placements checkboxes, optimisation goal",
      "Google copy: format selector, up to 15 headlines (30 chars), 4 descriptions (90 chars), display URL, CTA, Ad Assets (sitelinks, callouts, call, structured snippet)",
      "Meta copy: format selector (Feed, Story, Carousel), primary text (125 chars), headline (40 chars), link description, destination URL, CTA, creative image",
      "Live Preview always shows all selected platform tabs simultaneously — switching form tabs no longer hides previews",
      "Settings tab (renamed from Tracking) — Ad Accounts section: Google Customer ID + Meta Ad Account ID + Pixel ID, connected status badges, saved to ads-settings store",
      "Conversion Tracking section retained below Ad Accounts",
    ],
  },
  {
    version: "3.7.0",
    date: "2026-05-07",
    label: "Ads Manager — Full Rebuild",
    tag: "feature",
    items: [
      "Complete Ads Manager rewrite: Overview, My Campaigns, Tracking, Templates tabs",
      "4-step campaign wizard: Campaign Details → Targeting → Ad Copy & Creative → Review",
      "3-level hierarchy: Campaign → Ad Group → Ad with breadcrumb navigation on steps 2 and 3",
      "Step 2 restructured: Campaign Targeting (location, devices) separated from Ad Group 1 (name, audience, keywords, daily budget)",
      "Live preview panel: Google Search, Meta Feed, Instagram Story mockups update as you type",
      "AI copy generation with Business Brain context — auto-triggers on step 3 arrival if copy is empty",
      "Per-headline and per-description AI regenerate buttons (✦) for individual field rewrites",
      "Up to 15 headlines (30 chars), 4 descriptions (90 chars) per Google Search Ad",
      "Google Ad Assets section: 4 sitelinks (full URLs), 6 callouts, call asset, structured snippet, promotion text",
      "'✦ Generate Assets with AI' — creates all Google assets in one shot from Business Brain",
      "Budget: radio selector for Daily vs Total budget, currency in same row",
      "Business Brain auto-prefill on mount: location, audience, keywords, display URL, call asset (phone)",
      "'✦ Suggest with AI + Brain' button on step 2 generates campaign-specific targeting using AI + brain context",
      "15 industry ad templates (restaurant, home services, healthcare, general) with AI personalisation",
      "Approval workflow: Draft → Pending Approval → Approved → Published (role-gated)",
      "Campaign drill-down: Campaign → Ad Groups → Ads with inline status management",
      "Conversion Tracking tab: Google Tag + Meta Pixel code snippets, 6 conversion event examples",
      "Import React namespace — fixes React.Component and React.Fragment usage app-wide",
    ],
  },
  {
    version: "3.6.0",
    date: "2026-05-07",
    label: "Business Brain — Photos & Menu Improvements",
    tag: "feature",
    items: [
      "Photos tab: 4 import sources — From Device, Scan Website, Paste Image URL, Google Business Profile",
      "Website scanner: crawls entire site (gallery, menu, about pages) with Scan Entire Site toggle",
      "CDN URL surgery: automatically upgrades Wix, Squarespace, Shopify, Imgix, Cloudinary thumbnail URLs to full-size",
      "Prefers data-src/lazy-src over src, takes largest image from srcset, skips img tags declared < 200px",
      "Google Business Profile import: uses Places API to find business by name+city, imports up to 10 GBP photos",
      "Photo lightbox: click any thumbnail to view full-size with description + tags overlay",
      "Source filter tabs: All / From Device / From Website / From URL / Google Business",
      "Hover-to-delete: × button on mouseover, centred enlarge icon, inline Yes/No confirmation",
      "Photo upload routed through server (/api/upload-media) to bypass Supabase Storage RLS",
      "Menu tab: upload history with filename, date, item count, file size — click 'View' to open PDF inline",
      "PDF viewer modal: 85vh embed for PDF files, img for image menus, 'Open in new tab' link",
      "Original menu file uploaded to Supabase Storage on parse (workspaceId/menus/) for permanent access",
      "'Find photos for this menu' shortcut: switches to Photos, opens Scan Website pre-filled with site URL",
      "Menu parse: max_tokens raised 4000→8192, truncation repair salvages partial JSON, 30MB client-side limit",
      "Server body limit raised to 50MB to handle large base64 file uploads",
    ],
  },
  {
    version: "3.5.0",
    date: "2026-05-06",
    label: "Business Brain — Knowledge Base (Phase 1)",
    tag: "feature",
    items: [
      "Business Brain: niche-agnostic knowledge base that ingests business assets and powers grounded AI content generation",
      "Menu / Services tab: upload PDF or image → Claude vision extracts all sections, items, prices, and dietary tags; inline editing",
      "Team & People tab: add owner, chef, and staff with name, role, bio, years experience, fun fact, and photo",
      "Events & Specials tab: recurring events (with schedule), promotions, and seasonal specials with date ranges; active/inactive toggle",
      "Social Proof tab: awards, press mentions, certifications, partnerships, milestones; auto-populates top 4★+ reviews from Reviews module",
      "Photos tab: AI tags each Media Library photo with description, content tags, caption idea, and best platforms",
      "Website & Story tab: URL scraper imports story, USP, specialties, and tagline; fully editable fields",
      "Auto-sync on mount: silently pulls brand voice, top reviews, GBP hours/phone into brain — Refresh button for manual re-sync",
      "Health indicator row: 6 coloured dots show completion status for each brain source",
      "Brain store key: business-brain (workspace-scoped Supabase app_data)",
      "3 new server endpoints: /api/brain/parse-menu, /api/brain/scrape-website, /api/brain/tag-photo",
      "useAIContext wired: Kurb AI sees menu items, team, active events, proof, story, USP, top reviews, brand voice",
    ],
  },
  {
    version: "3.4.0",
    date: "2026-05-06",
    label: "Social Planner — Content Calendar & AI Planner",
    tag: "feature",
    items: [
      "Content Calendar: full month grid view with rich post cards showing time, 2-line text preview, thumbnail image, platform icons, and status badge",
      "Queue tab: sortable list of all scheduled posts — desktop table view and mobile card list",
      "AI Content Planner: generates a full week, month, or custom date range of posts — choose platforms, posting frequency (Bi-weekly / 1×/week / 2×/week / 3×/week), and content themes",
      "AI-generated posts include an imagePrompt per post; Pollinations.ai images generated and displayed as calendar thumbnails immediately, then permanently uploaded to Supabase Storage in background",
      "Schedule modal: compose a post, pick date/time (local timezone), add to calendar — past-date guard prevents invalid scheduling",
      "Post detail modal: full-width image preview, approve, reschedule (inline datetime), mark published, delete with inline confirmation",
      "Day overflow: clicking '+N more' on a calendar day opens a list of all posts for that day",
      "Modals shared across Calendar and Queue tabs — viewPost works from both",
      "useAIContext wired: Kurb AI sees scheduled/approved/published counts and upcoming 5 posts",
      "Performance: postsByDay computed via useMemo (O(1) per cell vs O(n×42) per render)",
    ],
  },
  {
    version: "3.3.0",
    date: "2026-05-06",
    label: "Auto-Image Generation in Content Writer",
    tag: "feature",
    items: [
      "✦ Auto-add Images button generates contextually relevant images for every section of a blog post or article",
      "AI writes one image prompt per section (hero + one per H2 heading, capped at 5) in a single fast call, then fires all image generations in parallel",
      "Images stream in inline as they load — skeleton placeholders appear immediately, replaced by real images as each resolves",
      "Images are embedded into saved content: 'Save to Library' writes image URLs into the markdown so the library view renders them inline",
      "MarkdownView now renders ![alt](url) image tags as actual <img> elements throughout the app",
      "Image quality follows workspace plan setting (Flux / DALL-E 3 / Imagen 3) — no user toggle",
    ],
  },
  {
    version: "3.2.0",
    date: "2026-05-06",
    label: "Image Generation — Imagen 3 + Admin Config",
    tag: "feature",
    items: [
      "Image generation integrated across Content Writer, Social Media, Website Builder, Ads Manager, and GBP Posts via GenerateImagePanel and MediaPickerModal Generate tab",
      "Three image engines: Flux (free, Pollinations), DALL-E 3 (OpenAI), Imagen 3 (Google) — configured by admin, not user-selectable",
      "Admin-controlled config rule: image quality lives in system_settings (global default), plan_templates, and workspaces (per-workspace override) — users see a read-only badge",
      "Plan Dashboard Settings tab: super admin can set global default and override per workspace",
      "Imagen 3 fixed: added 25-second timeout + Gemini Flash image generation as secondary fallback before Pollinations",
      "Supabase RLS boundary rule added to CLAUDE.md: all admin workspace/request mutations now route through server endpoints using service role — anon client silently discards cross-ownership writes",
      "Per-Workspace Override dropdown now correctly saves and loads (was missing image_quality column from both getWorkspaces and adminGetAllWorkspaces SELECT)",
      "White Label toggle and plan assignment (adminProvisionPlan, adminAssignTemplate) also fixed to use server-side updates",
    ],
  },
  {
    version: "3.1.0",
    date: "2026-05-05",
    label: "Team Invites — Permanent Fix & Workspace-Scoped AI",
    tag: "fix",
    items: [
      "Invite flow completely rearchitected: always uses inviteUserByEmail (24h token, scanner-safe) by detecting and deleting stub users created by old OTP calls",
      "Three invite cases handled cleanly: new user → proper invite email; stub user → delete + re-invite; active user → added to workspace directly with no email",
      "All team operations (accept invite, update role, remove member) now route through server with service role key — anon client was silently blocked by RLS",
      "AcceptInviteWelcome screen: new invited users set their name and password before entering the workspace, skipping onboarding entirely",
      "Expired/invalid magic link shows a clear 'Invite Link Expired' screen instead of falling through to onboarding",
      "AI Assistant is now fully workspace-scoped: keyed to workspace ID, session storage namespaced per workspace — switching workspaces gives a clean chat context",
      "Fixed: role changes and member removal were silently failing due to RLS on profiles table",
    ],
  },
  {
    version: "3.0.0",
    date: "2026-05-05",
    label: "Scroll Preservation & Focus Design Rule",
    tag: "fix",
    items: [
      "Universal scroll fix: onMouseDown preventDefault on all swap-out buttons (Revoke, Remove, Yes, No) stops browser from scrolling when a focused button is removed from the DOM",
      "useScrollPreserve() hook added for components with selects/dropdowns — captures main.scrollTop before each render and restores in useLayoutEffect",
      "Design rule documented in CLAUDE.md: buttons that get removed/replaced on click must always use onMouseDown preventDefault; buttons that stay in DOM (Send, Save, Submit) must never use it",
      "Plan Dashboard rename (was Admin Dashboard); workspace delete with inline confirmation",
      "Add Business: full inline onboarding embedded in the app shell, same 4 steps as new signup",
    ],
  },
  {
    version: "2.9.0",
    date: "2026-05-04",
    label: "Markdown Rendering & AI Panel Polish",
    tag: "improvement",
    items: [
      "Markdown rendering enforced app-wide — all AI-generated text now renders bold, bullets, headings, and links properly",
      "Fixed: Dashboard 'What To Do Today', campaign content, outreach templates, email body, review variations, and all AI action cards",
      "AI Assistant panel default width increased to 440px for better readability — still user-resizable and saved per device",
      "Dashboard daily priorities prompt improved — no longer outputs raw markdown headers",
    ],
  },
  {
    version: "2.8.0",
    date: "2026-05-04",
    label: "Deployment, White Label & Custom Domains",
    tag: "feature",
    items: [
      "Production deployment: Railway (persistent Express backend + cron) + Vercel (static frontend + custom domain routing)",
      "Live at app.getkurb.ai — Cloudflare DNS pointing to Railway",
      "White Label Phase 1: per-workspace branding — app name, tagline, logo, primary color, support email/URL, hide Powered by Kurb AI",
      "White label branding applies to sidebar, document title, and login screen — updates live without page reload",
      "Custom domain support: resellers can enter their own domain (e.g. app.theiragency.com) — app registers it with Vercel Domains API, provisions SSL automatically, shows DNS setup instructions",
      "Domain verification status badge: Registering → Pending DNS → Verified — with Check Again polling",
      "Admin Dashboard: super admin can view all workspaces, assign plan templates, toggle white label per workspace",
      "Plan Templates: reusable named module packages — admin creates once, assigns to any workspace in one click",
      "Super admin can switch into any workspace and see all its data (RLS policy + workspace key remount fix)",
      "Workspace switcher now forces all modules to remount — prevents stale data when switching between workspaces",
    ],
  },
  {
    version: "2.7.0",
    date: "2026-05-04",
    label: "Media Library",
    tag: "feature",
    items: [
      "Media Library module: upload images to Supabase Storage, browse in grid or list view, search by filename",
      "Workspace-scoped storage: each business has isolated media at {workspace_id}/{fileId}",
      "WebsiteBuilder image replacement: click any image in the site preview to open MediaPickerModal and replace it",
      "Undo toast: 8-second window to revert an image replacement — memory-only, no revision history overhead",
      "ContentWriter: Insert Image button appends markdown image tag to the editor",
    ],
  },
  {
    version: "2.6.0",
    date: "2026-05-04",
    label: "Roles & Permissions + Mobile Responsive",
    tag: "feature",
    items: [
      "Roles & Permissions: three roles — owner (full access), manager (all except billing/roles), staff (view + limited write)",
      "Team invite flow: owner invites via email → Supabase magic link → acceptPendingInvite() links new user to owner account",
      "Role-based module gating: canAccess() helper enforces permissions app-wide",
      "Mobile responsive layout complete across all modules — 360px–480px tested",
      "useIsMobile() hook (768px breakpoint) added to every component",
      "CSS auto-fit grids for sub-components that can't access isMobile",
    ],
  },
  {
    version: "2.5.0",
    date: "2026-05-03",
    label: "Kurb AI — Command in Chief",
    tag: "feature",
    items: [
      "Kurb AI Assistant is now the brain of the entire app — it knows what module is open, sees live data, and can take real actions in every module",
      "AI_MODULE_REGISTRY: single object centralises all per-module AI wiring — adding a new module to the AI now requires touching 4 places instead of 7",
      "Reviews: AI can respond to reviews (editable draft + one-click Post Reply), summarise sentiment, and generate review request messages",
      "CRM: AI can add contacts, look up contacts by name/email/phone, and append notes to existing contacts",
      "Campaigns: AI can show campaign status overview, pause and resume campaigns by name",
      "Bookings: AI can add reservations, show today's and upcoming bookings, and cancel by guest name",
      "Google Business Profile: AI generates GBP-optimised posts (≤1,400 chars, no hashtags, strong CTA) and Q&A pairs",
      "Dashboard morning briefing: AI auto-generates a proactive insights summary the first time the panel opens each session",
      "Live context bridges: each module registers a context function so the AI always sees real, current data (not just cached stats)",
      "Module refresh events: AI writes to the store then dispatches CustomEvents so modules re-render instantly after AI actions",
    ],
  },
  {
    version: "2.4.0",
    date: "2026-05-03",
    label: "Website Builder — Inline Editor & AI Editing",
    tag: "feature",
    items: [
      "Inline editor: hover any element in the website preview to see a floating badge showing section name and element type",
      "Click any heading, paragraph, button, link, or list item to edit it directly in the preview",
      "Rich-text formatting toolbar appears on click: Bold, Italic, Underline, Strikethrough, Align Left/Center/Right, Clear Formatting",
      "Active state indicators on toolbar buttons update in real-time as you select text",
      "Save/Cancel toolbar positioned below-right of the edited element — Save posts the edit, Cancel reverts",
      "Inline edits save to store without reloading the iframe — no scroll position loss",
      "Kurb AI Assistant renamed to 'AI Website Editor' in website module — context-aware of active page and all page names",
      "Website AI edits routed via classifier (website_edit intent) instead of keyword regex — more reliable, no false positives",
      "AI edit prompt includes the active page name and business name for more precise changes",
    ],
  },
  {
    version: "2.3.0",
    date: "2026-05-02",
    label: "Website Builder — Full Rebuild",
    tag: "feature",
    items: [
      "Complete Website Builder rebuild: 4-step intake wizard → multi-page AI generation → live preview → AI chat editing",
      "Business type selector (restaurant, home services, healthcare, retail, etc.) drives page templates and section choices",
      "Theme selector with 6 styles (Modern, Luxury, Fresh, Bold, Warm, Classic) and primary brand colour picker",
      "Page selector: choose which pages to generate — each gets its own full HTML file",
      "AI generation uses 12K output tokens per page for complete, untruncated results with Unsplash photography",
      "Multi-page preview with in-iframe navigation via postMessage — internal links switch tabs instead of navigating away",
      "Full Preview button opens all pages in a browser tab with working navigation",
      "Version history: save up to 10 named snapshots, restore any version with one click",
      "Direct iframe srcdoc updates bypass React render cycle for instant AI edits",
      "Conditional full-bleed layout — website module takes full width, other modules keep standard container",
    ],
  },
  {
    version: "2.0.0",
    date: "2026-05-01",
    label: "Default Mode & Advanced Mode — Dual UX",
    tag: "feature",
    items: [
      "Default Mode: simplified outcome-based view for 90% of users — 4 screens: Dashboard, Campaigns, Customers, Settings",
      "Advanced Mode: full marketing suite with all modules — same as before, now explicitly labeled",
      "Mode switching: new users land in Default Mode after onboarding; switch via Settings → 'Switch to Advanced Mode'",
      "Default Mode Campaigns: niche-specific outcome templates (Restaurant, Home Services, Healthcare) — pick a goal, answer 2-3 questions, launch",
      "Outcome templates: Fill Tables This Weekend, Get More Reservations, Promote a Special, Reactivate Past Guests, Boost Reviews, Grand Opening, Get More Calls Today, Book More Jobs, Emergency Leads Now, Seasonal Promotion, and more",
      "Default Mode Dashboard: greeting, 3 metric cards (Active Campaigns, Customers, New Messages), quick actions, running campaigns",
      "Default Mode Customers: unified Contacts + Bookings + Messaging Inbox in one screen",
    ],
  },
  {
    version: "2.0.1",
    date: "2026-05-01",
    label: "Messaging Inbox",
    tag: "feature",
    items: [
      "Unified Messaging Inbox inside Customers screen — FB Messenger, Instagram DMs, SMS, Google Business Messages",
      "Thread list with channel icons, unread badges, timestamps, and status tags (New / Lead / Handled)",
      "Conversation view: full message history, reply box, AI-assisted reply drafting (one click to generate a reply)",
      "Mark threads as New, Lead, or Handled — filter inbox by channel or status",
      "Mock data seeded on first load; persists to localStorage; real API integrations (Facebook Graph, Twilio) planned",
    ],
  },
  {
    version: "2.0.2",
    date: "2026-05-01",
    label: "AI Chat — Campaign Creation & Actions",
    tag: "improvement",
    items: [
      "AI chat can now CREATE full multi-channel campaigns: say 'create a Grand Opening campaign' and it generates content for all channels and saves to Campaign Manager",
      "Live per-channel progress shown in chat during generation (channel chips turn green as each completes)",
      "create_campaign action: AI picks closest template, generates all content, saves campaign as active with a completed run",
      "AI chat now knows about Default Mode, Advanced Mode, workspace switching, and all recent features",
      "userMode context passed to AI so it gives mode-aware answers",
    ],
  },
  {
    version: "2.0.3",
    date: "2026-05-01",
    label: "HubSpot-style UI Redesign",
    tag: "improvement",
    items: [
      "Full light theme: white cards (#ffffff), light gray background (#f4f6f9), dark text (#1c2437)",
      "Dark sidebar (#333333 space gray) with white text — HubSpot-style pill active indicator (rounded highlight, no left border)",
      "TopBar: full-width above content, with search/AI bar, notifications, settings shortcut, and profile dropdown",
      "Profile dropdown in TopBar: business name, email, role badge, edit profile, plans, mode switcher, sign out",
      "Sidebar logo: amber K square + Kurb AI wordmark at top of sidebar",
      "Sidebar is now full height (extends to very top of screen); TopBar spans only the content area",
      "Font switched to Inter (body/UI) + Plus Jakarta Sans (headings/display) — clean modern SaaS typography",
      "Workspace switcher added to Default Mode sidebar — both modes support multiple businesses",
    ],
  },
  {
    version: "2.0.4",
    date: "2026-05-01",
    label: "Site Audit Fix & Page-Level Issues",
    tag: "fix",
    items: [
      "Site audit (Research & Strategy) now returns correct score and stats — DataForSEO fields were nested under page_metrics, not root result",
      "Crawl progress polling fixed — audit no longer returns 'done' while still crawling",
      "Issues list now shows which specific pages have each issue — click any issue to expand and see affected URLs",
      "Affected page URLs are clickable links opening in a new tab",
    ],
  },
  {
    version: "1.3.0",
    date: "2026-04-30",
    label: "Reports Module",
    tag: "feature",
    items: [
      "New Reports module — tracks all activity across modules in a unified log",
      "Date range presets: Last 7 days, Last 30 days, Last month, Last 60 days, Custom range",
      "Type filter: Content, Reviews, Research, Campaigns, Bookings, Integration, CRM",
      "Summary bar shows per-type counts for the current filtered view",
      "Export PDF — opens print-ready report in new window, save as PDF via browser",
      "Email Report — opens mailto with top 30 activities formatted as text summary",
      "Copy Link — copies URL with filter params for sharing the same filtered view",
      "Activity logging wired into WordPress and Webhook publish actions",
    ],
  },
  {
    version: "1.2.0",
    date: "2026-04-30",
    label: "WordPress Integration — Post Type & Categories",
    tag: "improvement",
    items: [
      "Publish as Post or Page — post type selector in the Publish modal",
      "Category picker — fetches live categories from connected WordPress site",
      "Multi-select categories for posts; pages skip categories automatically",
      "Markdown-to-HTML conversion fixed before publishing (bold, headings, lists rendered correctly)",
      "Leading H1 stripped from content body — WordPress already renders the title separately",
      "Timeouts added to all WP API calls (15s test, 20s publish) to prevent hanging",
      "Server logs postType on each publish for debugging",
      "nodemon added — server auto-restarts on file changes during development",
    ],
  },
  {
    version: "1.1.0",
    date: "2026-04-30",
    label: "Integrations Module",
    tag: "feature",
    items: [
      "New Integrations module — connect external platforms to publish content",
      "WordPress integration: Application Password auth, publish posts and pages via REST API",
      "Webhook integration: send JSON payload to any HTTP endpoint (Zapier, Make, n8n)",
      "Test Connection verifies WordPress credentials before saving",
      "Publish modal: choose destination, post type, category, status (Draft / Publish Now / Schedule)",
      "Scheduled publishing via WordPress future status with datetime picker",
      "Coming-soon cards for Wix, Shopify, Webflow, Squarespace",
    ],
  },
  {
    version: "1.0.0",
    date: "2026-04-30",
    label: "Content Module — Library, Editor & Publish",
    tag: "feature",
    items: [
      "Content Library — saved pieces organised by type with search and filter",
      "Mini WYSIWYG editor (B, I, U, H1–H3, lists, undo/redo) using contentEditable",
      "Markdown preview with full inline formatting and heading styles",
      "Content generated from Research module is queued directly into Content",
      "Publish flow: open any piece → choose platform → set status → one-click publish",
      "mdToHtml() converter handles bold, italic, headings, and ordered/unordered lists",
    ],
  },
  {
    version: "0.9.0",
    date: "2026-04-29",
    label: "Research & Strategy Module",
    tag: "feature",
    items: [
      "Keyword research via DataForSEO: suggestions + Google Ads search volume merged",
      "Volume sparklines, competition score, and CPC shown per keyword",
      "Site audit via DataForSEO OnPage API (async task_post + polling)",
      "Competitor analysis via SERP — top 10 organic results with authority metrics",
      "Content idea generation: select keywords → choose content types → generate with AI",
      "Keyword lists and history management with save, delete, and multi-select",
      "Content ideas saved to Content module queue",
    ],
  },
  {
    version: "0.8.5",
    date: "2026-04-28",
    label: "Workspaces & Multi-Business",
    tag: "feature",
    items: [
      "Workspaces — manage multiple businesses from one account (sidebar switcher)",
      "Add Business launches full onboarding flow for each new workspace",
      "All data (reviews, CRM, campaigns, etc.) is isolated per workspace",
      "Workspace name auto-syncs from onboarding business name",
      "Supabase: workspaces + workspace_members tables with RLS",
      "store.js now keys all data by workspace_id instead of user_id",
    ],
  },
  {
    version: "0.8.0",
    date: "2026-04-27",
    label: "Roles & Permissions",
    tag: "feature",
    items: [
      "Three-tier role system: Owner, Manager, Staff",
      "Interactive permission matrix — toggle modules on/off per role",
      "Sidebar and pages gated by role — hidden items are fully inaccessible",
      "Team invite flow: send magic-link invite, auto-accept on sign-in",
      "Roles & Permissions page added to sidebar",
      "Role badge shown in sidebar account button",
      "Danger Zone (delete account, billing) restricted to Owner only",
    ],
  },
  {
    version: "0.7.0",
    date: "2026-04-26",
    label: "Onboarding — Google Business Autocomplete",
    tag: "improvement",
    items: [
      "Business Name field in onboarding searches Google Business Profile via DataForSEO",
      "Dropdown shows matching businesses with address, phone, and star rating",
      "Selecting a result auto-fills name, address, city, phone, and website URL",
      "Website URL field now populated from DataForSEO url field",
      "Clear button resets all auto-filled fields",
    ],
  },
  {
    version: "0.6.0",
    date: "2026-04-25",
    label: "Reviews — Sync, Pagination & Progress",
    tag: "improvement",
    items: [
      "Review sync switched to DataForSEO async task_post + task_get polling",
      "Animated progress bar during sync (ease-out curve, holds at 100% on complete)",
      "Fixed merge bug — full set of synced reviews now replaces previous DataForSEO reviews correctly",
      "Pagination added to All Reviews tab: 20 / 50 / 100 reviews per page",
      "Sync polls up to 3 minutes (36 x 5s) to handle large review sets",
    ],
  },
  {
    version: "0.5.0",
    date: "2026-04-24",
    label: "DataForSEO — Business Search Autocomplete",
    tag: "improvement",
    items: [
      "Business search in Reviews sync uses live dropdown (type-to-search, 600ms debounce)",
      "Results show business name, address, phone, and star rating",
      "Selecting a result locks the input; clear to search again",
      "Server: /api/business/search calls DataForSEO SERP Maps live/advanced",
    ],
  },
  {
    version: "0.4.0",
    date: "2026-04-23",
    label: "Supabase Auth & Team Management",
    tag: "feature",
    items: [
      "Supabase auth integrated — sign up, sign in (email/password + magic link + Google OAuth)",
      "profiles table: stores role, account_id, display_name",
      "app_data table: shared storage keyed by account_id so all team members share data",
      "team_invites table: invite flow with OTP magic link",
      "acceptPendingInvite() runs on sign-in to link invited users to the owner's account",
      "Settings page shows team members, pending invites, and remove/role-change controls",
    ],
  },
];

export const TAG_COLORS = {
  feature:     { bg: "#6366f118", border: "#6366f140", text: "#818cf8" },
  improvement: { bg: "#5a9e6d18", border: "#5a9e6d40", text: "#5a9e6d" },
  fix:         { bg: "#bb5a4418", border: "#bb5a4440", text: "#bb5a44" },
};

export const CHANGELOG_SUMMARY = CHANGELOG.slice(0, 10).map(r =>
  `v${r.version} (${r.date}) [${r.tag}] ${r.label}:\n  ${r.items.join("\n  ")}`
).join("\n\n");
